import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Terms of Use page
 */
export function TermsPage() {
  return (
    <div className="h-full flex flex-col overflow-hidden text-text-primary">
      {/* Header - fixed */}
      <header className="flex-shrink-0 bg-background/90 backdrop-blur-sm border-b border-surface">
        <div className="flex items-center gap-4 px-4 py-4">
          <Link
            to="/"
            className="p-2 hover:bg-surface rounded-lg transition-colors focus-visible-ring"
            aria-label="Back to home"
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-xl font-bold">Terms of Use</h1>
        </div>
      </header>

      {/* Content - scrollable */}
      <main className="flex-1 overflow-auto px-4 py-6 safe-bottom">
        <div className="prose prose-invert prose-sm">
          <p className="text-text-secondary text-sm mb-6">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">1. Acceptance of Terms</h2>
            <p className="text-text-secondary leading-relaxed">
              By accessing and using GPS Hunt (&quot;the App&quot;), you accept and agree to be bound by the terms 
              and provisions of this agreement. If you do not agree to these terms, please do not use this App.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">2. Description of Service</h2>
            <p className="text-text-secondary leading-relaxed">
              GPS Hunt is a location-based game that allows users to create and participate in 
              treasure hunts using GPS coordinates. The service requires access to your device&apos;s 
              location services and camera.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">3. User Responsibilities</h2>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>You must be at least 13 years old to use this App</li>
              <li>You are responsible for your physical safety while playing</li>
              <li>Do not trespass on private property</li>
              <li>Obey all local laws and regulations</li>
              <li>Do not create hunts in dangerous or inappropriate locations</li>
              <li>You are responsible for maintaining the confidentiality of your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">4. Content Guidelines &amp; Moderation</h2>
            <p className="text-text-secondary leading-relaxed mb-3">
              GPS Hunt uses automated content moderation to maintain a safe environment for all users.
              The following content is strictly prohibited and will be automatically filtered:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li><strong>Sexual content</strong> - Nudity, sexually explicit material, or suggestive content</li>
              <li><strong>Violent content</strong> - Gore, graphic violence, or threatening imagery</li>
              <li><strong>Hateful content</strong> - Content promoting discrimination, hatred, or harm against individuals or groups</li>
              <li><strong>Self-harm content</strong> - Content depicting or promoting self-injury</li>
              <li><strong>Illegal content</strong> - Content depicting illegal activities or contraband</li>
            </ul>
            <p className="text-text-secondary leading-relaxed mt-3">
              <strong className="text-white">Automated screening:</strong> All uploaded images are automatically 
              screened using AI content moderation. Images that violate these guidelines will be rejected and 
              deleted immediately. Repeated violations may result in account suspension.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">5. Safety Guidelines</h2>
            <p className="text-text-secondary leading-relaxed mb-3">
              While using GPS Hunt:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>Be aware of your surroundings at all times</li>
              <li>Do not use the App while driving or operating machinery</li>
              <li>Play in well-lit, public areas when possible</li>
              <li>Inform someone of your location when playing alone</li>
              <li>Respect wildlife and natural environments</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">6. Intellectual Property</h2>
            <p className="text-text-secondary leading-relaxed">
              The App and its original content, features, and functionality are owned by GPS Hunt 
              and are protected by international copyright, trademark, patent, trade secret, and 
              other intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">7. Limitation of Liability</h2>
            <p className="text-text-secondary leading-relaxed">
              GPS Hunt shall not be liable for any indirect, incidental, special, consequential, 
              or punitive damages resulting from your use of the App, including but not limited 
              to personal injury, property damage, or any other damages arising from playing the game.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">8. Changes to Terms</h2>
            <p className="text-text-secondary leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify users of any 
              changes by updating the &quot;Last updated&quot; date at the top of this page. Continued use 
              of the App after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">9. Contact Us</h2>
            <p className="text-text-secondary leading-relaxed">
              If you have any questions about these Terms, please contact us through the app.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}

export default TermsPage;
